/**
 * Created by neo on 11.01.21.
 */
import { useQuery } from '@tanstack/react-query';
import { makeAutoObservable } from 'mobx';
import { useBackend } from '../../Hooks/useBackend';
import { HttpBackend } from '../../Services/Http/HttpBackend';
import { Pageable } from '../Interfaces/Pageable';
import { ExploreCategoryLink, createExploreCategoryLink } from './ExploreCategoryLink';
import { ExploreContent, createExploreContent } from './ExploreContent';
import { isPublished } from '../../Utils/isPublished';
import { getAllExploreCategoriesV3, deriveRootCategories, resolveCategoryId } from './exploreCategoryTreeV3';

export type ExploreCategoryType =
  | 'preview'
  | 'big_preview'
  | 'single_exercises'
  | 'card_link'
  | 'category_group'
  | 'link_list'
  | 'recipe_recommendations'
  | 'recipe_favorites'
  | 'recipe_all'
  | 'tools'
  | 'blank';

export type ExploreCategoryQueryRequest = Pageable & {
  query?: string;
  parentId?: string;
  tags?: string[];
  root?: true;
  appVersion?: string;
  system?: 'ios' | 'android';
};

export type ExploreCategory = ExploreContent & {
  identifier: string;
  parentId?: string;
  parent?: ExploreCategory;
  link?: string;
  links: ExploreCategoryLink[];
};

export function createExploreCategory(json?: Partial<ExploreCategory>): ExploreCategory {
  return makeAutoObservable({
    ...createExploreContent({ ...json, contentType: 'category' }),
    identifier: json?.identifier ?? '',
    parentId: json?.parentId,
    parent: undefined as ExploreCategory | undefined,
    link: json?.link,
    links: (json?.links ?? []).map((l: any) => createExploreCategoryLink(l)),
  });
}

// Hooks
export function useExploreCategories(request?: Partial<ExploreCategoryQueryRequest>) {
  return useQuery({
    queryKey: ['exploreCategoriesV3', request],
    queryFn: async () => {
      const all = await getAllExploreCategoriesV3();
      if (request?.parentId) {
        const parentId = resolveCategoryId(all, request.parentId);
        return parentId ? all.filter((c) => (c.parentIds ?? []).includes(parentId)).filter(isPublished) : [];
      }
      if (request?.root) {
        return deriveRootCategories(all).filter(isPublished);
      }
      return all.filter(isPublished);
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useExploreCategory(id: string | undefined) {
  const backend = useBackend();
  return useQuery({
    queryKey: ['exploreCategoryV3', id],
    queryFn: () => backend.get(`/coach/explore/v3/categories/${id}`).then((res) => createExploreCategory(res)),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

// Legacy static API - prefer hooks above
export const ExploreCategoryApi = {
  async find(request?: Partial<ExploreCategoryQueryRequest>): Promise<ExploreCategory[]> {
    const all = await getAllExploreCategoriesV3();
    if (request?.parentId) {
      const parentId = resolveCategoryId(all, request.parentId);
      return parentId ? all.filter((c) => (c.parentIds ?? []).includes(parentId)) : [];
    }
    if (request?.root) {
      return deriveRootCategories(all);
    }
    return all;
  },

  findOne(id: string): Promise<ExploreCategory> {
    return HttpBackend.get(`/coach/explore/v3/categories/${id}`).then((res) => createExploreCategory(res));
  },

  findAll(ids: string[]): Promise<ExploreCategory[]> {
    return HttpBackend.get(`/coach/explore/v3/categories/findAll/${ids.join(',')}`).then((result) =>
      (result ?? []).map((res: any) => createExploreCategory(res)),
    );
  },
};
